import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Badge,
  useTheme,
  Tooltip,
  Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Computer as ComputerIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Language as WebIcon,
  Router as NetworkIcon,
  Dns as DnsIcon,
  Security as SecurityIcon,
  Category as DefaultIcon,
} from '@mui/icons-material';
import { Exporter, Category } from '../../types/exporter';
import { ResponsiveCardGrid } from 'components/shared';

export interface CategoryHealthCardsProps {
  exporters: Exporter[];
  alertCounts: Record<number, number>;
  categories: Category[];
  onCategoryClick?: (categoryName: string) => void;
  selectedCategory?: string;
  loading?: boolean;
}

/**
 * Get icon component based on category icon string
 */
function getCategoryIcon(iconName?: string): React.ReactNode {
  switch (iconName?.toLowerCase()) {
    case 'computer':
    case 'server':
      return <ComputerIcon />;
    case 'storage':
    case 'database':
      return <StorageIcon />;
    case 'cloud':
      return <CloudIcon />;
    case 'web':
    case 'website':
    case 'language':
      return <WebIcon />;
    case 'network':
    case 'router':
      return <NetworkIcon />;
    case 'dns':
      return <DnsIcon />;
    case 'security':
      return <SecurityIcon />;
    default:
      return <DefaultIcon />;
  }
}

interface CategoryCardProps {
  category: Category;
  exporters: Exporter[];
  alertCount: number;
  isSelected?: boolean;
  onClick?: () => void;
}

function CategoryCard({ category, exporters, alertCount, isSelected, onClick }: CategoryCardProps) {
  const theme = useTheme();

  const upCount = exporters.filter((e) => e.status === 'up').length;
  const downCount = exporters.filter((e) => e.status === 'down').length;
  const totalCount = exporters.length;
  const healthPercentage = totalCount > 0 ? (upCount / totalCount) * 100 : 0;

  const hasIssues = downCount > 0 || alertCount > 0;
  const accentColor = hasIssues ? theme.palette.error.main : theme.palette.primary.main;

  return (
    <Paper
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        p: 2,
        borderRadius: 2,
        border: isSelected ? `2px solid ${accentColor}` : `1px solid ${theme.palette.divider}`,
        backgroundColor: isSelected ? alpha(accentColor, 0.04) : undefined,
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[4],
          borderColor: alpha(accentColor, 0.5),
        },
      }}
    >
      {/* Alert Badge */}
      {alertCount > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
          }}
        >
          <Tooltip title={`${alertCount} active alerts`}>
            <Badge
              badgeContent={alertCount}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                },
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Category Icon + Name */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(
              hasIssues ? theme.palette.error.main : theme.palette.primary.main,
              0.1
            ),
            color: hasIssues ? theme.palette.error.main : theme.palette.primary.main,
            mr: 1.5,
          }}
        >
          {getCategoryIcon(category.icon)}
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {category.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalCount} exporter{totalCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Health Bar */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Health
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: healthPercentage === 100
                ? theme.palette.success.main
                : healthPercentage >= 80
                ? theme.palette.warning.main
                : theme.palette.error.main,
              fontWeight: 'medium',
            }}
          >
            {healthPercentage.toFixed(0)}%
          </Typography>
        </Box>
        <Box sx={{ position: 'relative', height: 8, borderRadius: 4, overflow: 'hidden' }}>
          {/* Background */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: alpha(theme.palette.error.main, 0.2),
            }}
          />
          {/* Healthy portion (green) */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: `${healthPercentage}%`,
              backgroundColor: theme.palette.success.main,
              transition: 'width 0.3s ease-in-out',
            }}
          />
        </Box>
      </Box>

      {/* Status Summary */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: theme.palette.success.main,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {upCount} up
          </Typography>
        </Box>
        {downCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: theme.palette.error.main,
              }}
            />
            <Typography variant="caption" color="error">
              {downCount} down
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

/**
 * Category Health Cards
 *
 * Grid of cards, one per category, showing:
 * - Category icon + name
 * - Exporter count
 * - Health bar (% up vs down)
 * - Alert count badge
 * - Clickable to filter by that category
 *
 * @example
 * <CategoryHealthCards
 *   exporters={exporters}
 *   alertCounts={alertCounts}
 *   categories={categories}
 *   onCategoryClick={(categoryId) => setFilters({ category_id: categoryId })}
 * />
 */
export function CategoryHealthCards({
  exporters,
  alertCounts,
  categories,
  onCategoryClick,
  selectedCategory,
  loading = false,
}: CategoryHealthCardsProps) {
  const theme = useTheme();

  // Group exporters by category and calculate stats
  const categoryStats = useMemo(() => {
    const stats: Record<number, { exporters: Exporter[]; alertCount: number }> = {};

    // Initialize all categories
    categories.forEach((cat) => {
      stats[cat.id] = { exporters: [], alertCount: 0 };
    });

    // Group exporters by category
    exporters.forEach((exporter) => {
      if (exporter.category_id && stats[exporter.category_id]) {
        stats[exporter.category_id].exporters.push(exporter);
        stats[exporter.category_id].alertCount += alertCounts[exporter.id] || 0;
      }
    });

    return stats;
  }, [exporters, alertCounts, categories]);

  // Sort categories by issues (most issues first), then by exporter count
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const statsA = categoryStats[a.id] || { exporters: [], alertCount: 0 };
      const statsB = categoryStats[b.id] || { exporters: [], alertCount: 0 };

      // First sort by issues (down + alerts)
      const issuesA =
        statsA.exporters.filter((e) => e.status === 'down').length + statsA.alertCount;
      const issuesB =
        statsB.exporters.filter((e) => e.status === 'down').length + statsB.alertCount;

      if (issuesA !== issuesB) return issuesB - issuesA;

      // Then by exporter count
      return statsB.exporters.length - statsA.exporters.length;
    });
  }, [categories, categoryStats]);

  // Filter out empty categories
  const nonEmptyCategories = sortedCategories.filter(
    (cat) => categoryStats[cat.id]?.exporters.length > 0
  );

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <ResponsiveCardGrid columns={{ xs: 2, sm: 3, md: 4, lg: 5 }} gap={2}>
          {[0, 1, 2, 3].map((index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Skeleton animation="wave" variant="rounded" width={44} height={44} sx={{ mr: 1.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton animation="wave" variant="text" width="70%" height={20} />
                  <Skeleton animation="wave" variant="text" width="40%" height={16} />
                </Box>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Skeleton animation="wave" variant="text" width={40} />
                  <Skeleton animation="wave" variant="text" width={30} />
                </Box>
                <Skeleton animation="wave" variant="rectangular" height={8} sx={{ borderRadius: 4 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                <Skeleton animation="wave" variant="circular" width={8} height={8} />
                <Skeleton animation="wave" variant="text" width={30} />
              </Box>
            </Paper>
          ))}
        </ResponsiveCardGrid>
      </Box>
    );
  }

  if (nonEmptyCategories.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <ResponsiveCardGrid columns={{ xs: 2, sm: 3, md: 4, lg: 5 }} gap={2}>
        {nonEmptyCategories.map((category) => {
          const stats = categoryStats[category.id];
          return (
            <CategoryCard
              key={category.id}
              category={category}
              exporters={stats.exporters}
              alertCount={stats.alertCount}
              isSelected={selectedCategory === category.name}
              onClick={() => onCategoryClick?.(
                selectedCategory === category.name ? 'all' : category.name
              )}
            />
          );
        })}
      </ResponsiveCardGrid>
    </Box>
  );
}

export default CategoryHealthCards;
